import { CalendarRange, ChevronDown } from 'lucide-react';
import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatMonthsLabel, monthLabel, monthOptions } from '@/lib/months';

/** Meses del embudo con casillas (pueden ser salteados) y atajos; los últimos 12 meses. */
export function MonthPicker({
  value,
  onChange,
  count = 12,
}: {
  value: string[];
  onChange: (months: string[]) => void;
  count?: number;
}) {
  const options = useMemo(() => monthOptions(count), [count]);
  const toggle = (m: string) =>
    onChange(value.includes(m) ? value.filter((x) => x !== m) : [...value, m].sort());
  const last = (n: number) => onChange(options.slice(0, n).sort());
  const thisYear = () =>
    onChange(options.filter((m) => m.startsWith(options[0]!.slice(0, 4))).sort());
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="h-10 w-full justify-between font-normal"
          aria-label="Meses"
          data-testid="month-picker"
        >
          <span className="flex min-w-0 items-center gap-2">
            <CalendarRange className="text-muted-foreground h-4 w-4 shrink-0" />
            <span className="truncate">{formatMonthsLabel(value)}</span>
          </span>
          <ChevronDown className="text-muted-foreground h-4 w-4 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64">
        <DropdownMenuLabel>Atajos</DropdownMenuLabel>
        <DropdownMenuItem onSelect={() => last(1)}>Este mes</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => last(3)}>Últimos 3 meses</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => last(6)}>Últimos 6 meses</DropdownMenuItem>
        <DropdownMenuItem onSelect={thisYear}>Este año</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Marca los meses que quieras (pueden ser salteados)</DropdownMenuLabel>
        <div className="max-h-64 overflow-y-auto">
          {options.map((m) => (
            <DropdownMenuCheckboxItem
              key={m}
              checked={value.includes(m)}
              onCheckedChange={() => toggle(m)}
              onSelect={(e) => e.preventDefault()}
              data-testid="month-option"
            >
              {monthLabel(m)}
            </DropdownMenuCheckboxItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
